import React from 'react';

import { ForgeUIExtensionPointProvider } from '@atlassian/forge-ui/provider/ForgeUIExtensionPointProvider';
import type { ForgeUIExtensionQueryOptions } from '@atlassian/forge-ui/provider/ForgeUIExtensionPointProvider';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { getAnalyticsWebClient } from '@confluence/analytics-web-client';
import { fg } from '@confluence/feature-gating';

import { getProductEnvironment } from './utils/environmentMapping.ts';
import type { Extension } from './types.ts';
import { useExtensionList } from './hooks/useExtensionList.tsx';
import type { ForgeModules } from './ForgeModules.ts';

export interface DecoratorProps {
	title: string;
	extension: Extension;
	children?: React.ReactNode;
}

export type RenderForgeExtensions = (
	extensions: ReadonlyArray<Extension>,
	loading: boolean,
	error?: Error,
) => any;

type ForgeUIExtensionProviderWrapperProps = React.PropsWithChildren<{
	moduleType: string;
	noAccessRenderFallback?: RenderForgeExtensions;
	/** The localId of a bodied macro that contains embedded macros rendered via FAUS */
	localId?: string;
}>;

export const ForgeUIExtensionProviderWrapper = ({
	children,
	moduleType,
	noAccessRenderFallback = () => null,
	localId,
}: ForgeUIExtensionProviderWrapperProps): any => {
	const { environment, userId } = useSessionData();
	const isAnonymous = !userId;

	if (isAnonymous && !fg('unskip_useextensionlist_unlicensed_support')) {
		return noAccessRenderFallback([], false);
	}

	return (
		<ForgeUIExtensionPointProvider
			analyticsWebClient={getAnalyticsWebClient()}
			environment={getProductEnvironment(environment)}
			product="confluence"
			page={moduleType}
			localId={localId ?? ''}
		>
			{children}
		</ForgeUIExtensionPointProvider>
	);
};

type ForgeUIExtensionsWrapperProps = {
	moduleType: ForgeModules;
	render: RenderForgeExtensions;
	returnBlockedExtensions?: boolean;
	/** The localId of a bodied macro that contains embedded macros rendered via FAUS */
	localId?: string;
};

export interface PropsNext {
	moduleType: ForgeModules;
	render: RenderForgeExtensions;
	queryOptions?: ForgeUIExtensionQueryOptions;
	returnBlockedExtensions?: boolean;
}

export const ForgeUIExtensions = ({
	moduleType,
	render,
	queryOptions,
	returnBlockedExtensions = false,
}: PropsNext): any => {
	const { extensions, loading, error } = useExtensionList({
		moduleType,
		queryOptions,
		returnBlockedExtensions,
	});
	return render(sortExtensionsAlphabeticalTitle(extensions), loading, error);
};

export const ForgeUIExtensionsWrapper = (
	props: ForgeUIExtensionsWrapperProps,
): React.JSX.Element => {
	return (
		<ForgeUIExtensionProviderWrapper
			moduleType={props.moduleType}
			noAccessRenderFallback={props.render}
			localId={props.localId}
		>
			<ForgeUIExtensions {...props} />
		</ForgeUIExtensionProviderWrapper>
	);
};

export const sortExtensionsAlphabeticalTitle = (extensions: Extension[]): Extension[] => {
	return extensions.slice().sort((a, b) => {
		const titleA = a.properties.title;
		const titleB = b.properties.title;

		if (titleA && !titleB) {
			return -1;
		} else if (titleB && !titleA) {
			return 1;
		}
		if (!(titleA && titleB)) {
			return 0;
		}

		return titleA.localeCompare(titleB);
	});
};
